// @ adapter 二级菜单 ——
//
// stage = "adapter": 选 adapter id
// stage = "args":    已选 adapter, 选其 usages 模板
//
// 所有 key handling 由父组件 (Composer) 统一处理, 这里只渲染 + 鼠标事件。

import { useMemo } from "react";

import type { RoomAdapter, UsageOption } from "../lib/auth-api";
import { adapterMeta } from "../lib/adapter-meta";

export type MentionStage = "adapter" | "args";

export interface MentionDropdownProps {
  stage: MentionStage;
  /** stage="adapter" 时: 当前输入 token (去掉 @) */
  query: string;
  /** stage="adapter" 时: 候选 adapter 列表（来自 /api/rooms/:r/adapters） */
  adapters: RoomAdapter[];
  /** stage="args" 时: 已选的 adapter, 显其 usages */
  activeAdapter?: RoomAdapter;
  /** stage="args" 时: 当前输入的子参数 token, 用于过滤 usages */
  argsQuery?: string;
  /** 受控的高亮 index */
  active: number;
  onActiveChange(i: number): void;
  /** stage="adapter": 传 adapter.id; stage="args": 传 usage.template */
  onPick(value: string): void;
}

/** 过滤 stage 1 候选 */
export function filterAdapters(
  list: RoomAdapter[],
  query: string,
): RoomAdapter[] {
  const q = query.toLowerCase();
  return list.filter((a) => a.id.toLowerCase().includes(q));
}

/** 过滤 stage 2 候选 */
export function filterUsages(usages: UsageOption[], query: string): UsageOption[] {
  if (!query) return usages;
  const q = query.toLowerCase();
  return usages.filter(
    (u) =>
      u.label.toLowerCase().includes(q) || u.template.toLowerCase().includes(q),
  );
}

export function MentionDropdown(props: MentionDropdownProps) {
  if (props.stage === "adapter") return <AdapterList {...props} />;
  return <UsageList {...props} />;
}

function AdapterList({
  query,
  adapters,
  active,
  onActiveChange,
  onPick,
}: MentionDropdownProps) {
  const filtered = useMemo(() => filterAdapters(adapters, query), [adapters, query]);
  if (filtered.length === 0) return null;
  return (
    <ul className="mention-dropdown" role="listbox" aria-label="adapter 候选">
      <li className="mention-dropdown__hint">
        ↑↓ 选择 · Enter / Tab 选定 · Esc 取消
      </li>
      {filtered.map((a, i) => {
        // server 端 meta 优先, 没有走 hardcoded fallback
        const fallback = adapterMeta(a.id);
        const desc = a.description ?? fallback.description;
        return (
          <li
            key={a.id}
            role="option"
            aria-selected={i === active}
            className={
              i === active
                ? "mention-dropdown__item is-active"
                : "mention-dropdown__item"
            }
            onMouseEnter={() => onActiveChange(i)}
            onMouseDown={(e) => {
              e.preventDefault();
              onPick(a.id);
            }}
          >
            <span className="mention-dropdown__row">
              <span className="mention-dropdown__at">@</span>
              <span className="mention-dropdown__id">{a.id}</span>
              {a.trust_scope === "confirm-each" && (
                <span className="mention-dropdown__scope">审批</span>
              )}
            </span>
            <span className="mention-dropdown__desc">{desc}</span>
          </li>
        );
      })}
    </ul>
  );
}

function UsageList({
  activeAdapter,
  argsQuery,
  active,
  onActiveChange,
  onPick,
}: MentionDropdownProps) {
  const filtered = useMemo(
    () => (activeAdapter ? filterUsages(activeAdapter.usages, argsQuery ?? "") : []),
    [activeAdapter, argsQuery],
  );
  if (!activeAdapter || filtered.length === 0) return null;
  return (
    <ul className="mention-dropdown" role="listbox" aria-label="参数候选">
      <li className="mention-dropdown__hint">
        <code>@{activeAdapter.id}</code> 参数 · ↑↓ 选 · Enter / Tab 插入 · Esc 跳过
      </li>
      {filtered.map((u, i) => (
        <li
          key={u.label}
          role="option"
          aria-selected={i === active}
          className={
            i === active
              ? "mention-dropdown__item is-active"
              : "mention-dropdown__item"
          }
          onMouseEnter={() => onActiveChange(i)}
          onMouseDown={(e) => {
            e.preventDefault();
            onPick(u.template);
          }}
        >
          <span className="mention-dropdown__row">
            <code className="mention-dropdown__usage-label">{u.label}</code>
          </span>
          {u.description && (
            <span className="mention-dropdown__desc">{u.description}</span>
          )}
        </li>
      ))}
    </ul>
  );
}
